import { useCallback } from 'react'
import { PageHead } from '@components/PageHead'
import { useDisplayName } from '@features/account/hooks/useDisplayName'
import { useTrustedDevices } from '@features/devices/hooks/useTrustedDevices'
import { useHomeData, type UseHomeDataOptions } from '@features/vault/hooks/useHomeData'
import { VaultItemAvatar } from '@features/vault/components/VaultItemAvatar'
import { VaultStrengthBadge } from '@features/vault/components/VaultStrengthBadge'
import { type VaultItem } from '@features/vault/types'
import { copyToClipboard, getEffectiveClipboardTtlSec } from '@services/clipboard'
import { EmptyState } from '@shared/ui/EmptyState'
import { Notice } from '@shared/ui/Notice'
import { StatCard } from '@shared/ui/StatCard'
import { useToast } from '@shared/ui/useToast'
import { getGreetingBand } from '@utils/greeting'
import { Icon, type IconName } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * ホームダッシュボード (T-D-Home、xlsx No.26、L-6 home route 解消)。
 *
 * design/js/screens-home.jsx (Home) を移植。
 * 設計判断:
 *   - greeting の name は sessionStore.material.email のローカルパートを使用。
 *     /api/users/me は未実装のため、Phase 3 Settings 実装時に displayName に差し替え予定。
 *   - StatCard (セキュリティスコア / 弱いパスワード / アクティブ Send) は Phase 3 Later のため '—' 表示。
 *   - アクティビティ履歴は backend API なし → EmptyState (準備中メッセージ)。
 */

const previewFor = (item: VaultItem): string => {
  const p = item.payload
  if (p.type === 'login') return p.username ?? p.url ?? ''
  if (p.type === 'card') return p.cardHolder ?? ''
  if (p.type === 'note') return (p.body ?? '').slice(0, 40).replace(/\n+/gu, ' ')
  if (p.type === 'identity') return [p.firstName, p.lastName].filter(Boolean).join(' ')

  return ''
}

export interface HomePageProps {
  /** テスト用 DI。本番では省略。 */
  homeDataOptions?: UseHomeDataOptions
}

export const HomePage = ({ homeDataOptions }: HomePageProps = {}): JSX.Element => {
  const { t } = useTranslation()
  const { show: showToast } = useToast()
  const state = useHomeData(homeDataOptions)

  // 表示名: account profile.name → email local part → '' の順で fallback。
  const name = useDisplayName()

  // T-H1: ホームにサインイン中のデバイス一覧を表示 (read-only、管理は Settings へ)。
  const devices = useTrustedDevices()

  const onCopy = useCallback(
    async (item: VaultItem): Promise<void> => {
      const p = item.payload
      if (p.type !== 'login') return
      const value = p.username ?? p.password
      if (!value) return
      try {
        await copyToClipboard(value)
        showToast({
          titleKey: 'vault.toast.copied_username',
          titleVars: { ttl: getEffectiveClipboardTtlSec() },
          tone: 'success',
          dedupe: true,
        })
      } catch {
        showToast({ titleKey: 'vault.toast.copy_failed', tone: 'danger', dedupe: true })
      }
    },
    [showToast],
  )

  const securityRows: readonly {
    ico: IconName
    labelKey: string
    value: string | number
    tone: 'red' | 'amber' | 'green'
  }[] =
    state.kind === 'ready'
      ? [
          {
            ico: 'shield-alert',
            labelKey: 'home.security.weak',
            value: state.security.weakCount,
            tone: 'red',
          },
          {
            ico: 'refresh',
            labelKey: 'home.security.reused',
            value: state.security.reusedCount,
            tone: 'amber',
          },
          {
            ico: 'shield-check',
            labelKey: 'home.security.score',
            value: `${state.security.score} / 100`,
            tone: 'green',
          },
        ]
      : [
          { ico: 'shield-alert', labelKey: 'home.security.weak', value: '…', tone: 'red' },
          { ico: 'refresh', labelKey: 'home.security.reused', value: '…', tone: 'amber' },
          { ico: 'shield-check', labelKey: 'home.security.score', value: '…', tone: 'green' },
        ]

  return (
    <div>
      <PageHead
        title={t(`home.greeting.${getGreetingBand(new Date())}`, { name })}
        sub={t('home.sub')}
        actions={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                window.location.hash = 'tools/generator'
              }}
            >
              <Icon name="key" aria-hidden="true" />
              {t('home.action.generate')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                window.location.hash = 'vault/new'
              }}
            >
              <Icon name="plus" aria-hidden="true" />
              {t('home.action.new_item')}
            </button>
          </>
        }
      />

      <div className="page-body">
        {state.kind === 'error' && (
          <Notice tone="danger" ico="alert">
            {t(state.messageKey)}
          </Notice>
        )}

        {(state.kind === 'loading' || state.kind === 'ready') && (
          <>
            {/* KPI StatCards */}
            <div className="stat-grid home-stat-grid">
              <StatCard
                label={t('home.stat.vault_items')}
                value={state.kind === 'ready' ? state.count : '…'}
                ico="vault"
                iconTone="blue"
              />
              <StatCard
                label={t('home.stat.security_score')}
                value={state.kind === 'ready' ? `${state.security.score}` : '…'}
                ico="shield-check"
                iconTone="green"
              />
              <StatCard
                label={t('home.stat.weak_passwords')}
                value={state.kind === 'ready' ? state.security.weakCount : '…'}
                ico="shield-alert"
                iconTone="red"
              />
              <StatCard
                label={t('home.stat.reused_passwords')}
                value={state.kind === 'ready' ? state.security.reusedCount : '…'}
                ico="refresh"
                iconTone="amber"
              />
            </div>

            {/* Row 1: 最近使ったアイテム + セキュリティ推奨 */}
            <div className="home-grid">
              {/* 最近使ったアイテム */}
              <div className="card">
                <div className="card-head">
                  <div>
                    <h3>{t('home.recent.title')}</h3>
                    <div className="ch-sub">{t('home.recent.sub')}</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      window.location.hash = 'vault'
                    }}
                  >
                    {t('home.recent.view_all')}
                    <Icon name="arrow-right" aria-hidden="true" />
                  </button>
                </div>
                {state.kind === 'ready' && state.recent.length === 0 && (
                  <div className="home-card-empty">
                    <EmptyState
                      icon="vault"
                      title={t('vault.list.empty.title')}
                      sub={t('vault.list.empty.subtitle')}
                    />
                  </div>
                )}
                {state.kind === 'ready' &&
                  state.recent.map(item => (
                    <div
                      key={item.id}
                      className="card-row home-recent-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        window.location.hash = `vault/${item.id}`
                      }}
                      onKeyDown={ev => {
                        if (ev.key === 'Enter' || ev.key === ' ') {
                          ev.preventDefault()
                          window.location.hash = `vault/${item.id}`
                        }
                      }}
                      aria-label={item.payload.name}
                    >
                      <VaultItemAvatar name={item.payload.name} />
                      <div className="meta">
                        <div className="nm">{item.payload.name}</div>
                        {previewFor(item) && <div className="sb">{previewFor(item)}</div>}
                      </div>
                      <VaultStrengthBadge item={item} />
                      <div
                        className="acts"
                        onClick={ev => ev.stopPropagation()}
                        onKeyDown={ev => ev.stopPropagation()}
                        role="presentation"
                      >
                        {item.payload.type === 'login' &&
                          (item.payload.username ?? item.payload.password) && (
                            <button
                              type="button"
                              className="icon-btn sm"
                              title={t('home.copy')}
                              aria-label={t('home.copy')}
                              onClick={() => void onCopy(item)}
                            >
                              <Icon name="copy" />
                            </button>
                          )}
                        <button
                          type="button"
                          className="icon-btn sm"
                          title={t('vault.list.row.open_detail')}
                          aria-label={t('vault.list.row.open_detail')}
                          onClick={() => {
                            window.location.hash = `vault/${item.id}`
                          }}
                        >
                          <Icon name="chevron-right" />
                        </button>
                      </div>
                    </div>
                  ))}
              </div>

              {/* セキュリティ推奨 */}
              <div className="card">
                <div className="card-head">
                  <h3>{t('home.security.title')}</h3>
                </div>
                <div className="home-security-rows">
                  {securityRows.map(row => (
                    <div
                      key={row.labelKey}
                      className="home-security-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        window.location.hash = 'reports'
                      }}
                      onKeyDown={ev => {
                        if (ev.key === 'Enter' || ev.key === ' ') {
                          ev.preventDefault()
                          window.location.hash = 'reports'
                        }
                      }}
                    >
                      <span className={`home-security-ico is-${row.tone}`} aria-hidden="true">
                        <Icon name={row.ico} />
                      </span>
                      <span className="home-security-label">{t(row.labelKey)}</span>
                      <span className="tag">{row.value}</span>
                    </div>
                  ))}
                </div>
                <div className="home-security-footer">
                  <button
                    type="button"
                    className="btn btn-secondary btn-block"
                    onClick={() => {
                      window.location.hash = 'reports'
                    }}
                  >
                    {t('home.security.view_reports')}
                  </button>
                </div>
              </div>
            </div>

            {/* Row 2: お気に入り + 最近のアクティビティ */}
            <div className="home-grid">
              {/* お気に入り */}
              <div className="card">
                <div className="card-head">
                  <h3>{t('home.favorites.title')}</h3>
                </div>
                {state.kind === 'ready' && state.favorites.length === 0 && (
                  <div className="home-card-empty">
                    <EmptyState
                      icon="star"
                      title={t('home.favorites.empty_title')}
                      sub={t('home.favorites.empty_sub')}
                    />
                  </div>
                )}
                {state.kind === 'ready' && state.favorites.length > 0 && (
                  <div className="fav-grid">
                    {state.favorites.map(item => (
                      <div
                        key={item.id}
                        className="card fav-item"
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          window.location.hash = `vault/${item.id}`
                        }}
                        onKeyDown={ev => {
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.preventDefault()
                            window.location.hash = `vault/${item.id}`
                          }
                        }}
                        aria-label={item.payload.name}
                      >
                        <VaultStrengthBadge item={item} />
                        <VaultItemAvatar name={item.payload.name} size="md" />
                        <div className="fav-item-name">
                          <span className="fav-item-name-text">{item.payload.name}</span>
                          <span className="vault-row-fav" aria-hidden="true">
                            <Icon name="star" />
                          </span>
                        </div>
                        {previewFor(item) && <div className="fav-item-sub">{previewFor(item)}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 最近サインインしたデバイス (T-H1、直近 3 件、管理は Settings へ) */}
              <div className="card" data-testid="home-devices-card">
                <div className="card-head">
                  <h3>{t('home.devices.title')}</h3>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      window.location.hash = 'settings/security'
                    }}
                    data-testid="home-devices-manage"
                  >
                    {t('home.devices.manage')}
                  </button>
                </div>
                {devices.state.kind === 'loading' && (
                  <div className="home-card-empty">
                    <span className="settings-item-sub">{t('home.devices.loading')}</span>
                  </div>
                )}
                {devices.state.kind === 'error' && (
                  <div className="home-card-empty">
                    <span className="settings-item-sub">{t('home.devices.error')}</span>
                  </div>
                )}
                {devices.state.kind === 'ready' && devices.state.devices.length === 0 && (
                  <div className="home-card-empty">
                    <EmptyState
                      icon="shield"
                      title={t('home.devices.empty_title')}
                      sub={t('home.devices.empty_sub')}
                    />
                  </div>
                )}
                {devices.state.kind === 'ready' && devices.state.devices.length > 0 && (
                  <div className="home-devices-list">
                    {/* listByUser は lastUsedAt desc 順。全件表示し、高さ超過分はスクロール。 */}
                    {devices.state.devices.map(d => (
                      <div key={d.id} className="home-device-row">
                        <div className="home-device-icon" aria-hidden="true">
                          <Icon name="key" />
                        </div>
                        <div className="meta">
                          <div className="nm">
                            {d.parsedUa.display ?? t('home.devices.unknown_ua')}
                            {d.isCurrent && (
                              <span className="tag good home-device-current">
                                {t('home.devices.current')}
                              </span>
                            )}
                          </div>
                          <div className="sb">{d.ipPrefix ?? '—'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
